/**
 * VersionPickerModal — select source version for offer duplication.
 *
 * Shown when an offer has >= 2 PUBLISHED versions so the user can pick
 * which version to duplicate from. Default-selected = newest version.
 *
 * Pattern: custom role="dialog" + aria-modal (same as DeleteVersionDialog,
 * OrderDetailModal, DeleteConfirmation in OfferList).
 *
 * @see spec docs/specifications/2026-05-28-wybor-zrodlowej-wersji-oferty/spec.md
 */
import React, { useEffect, useRef, useState } from "react";
import { OfferVersion } from "../../services/hotelApi";
import { formatCurrency } from "../../utils/formatCurrency";

export interface VersionPickerModalProps {
  versions: OfferVersion[];
  onConfirm: (versionId: string) => void;
  onCancel: () => void;
}

function formatPublishedAt(publishedAt?: string | null): string {
  if (!publishedAt) return "—";
  const d = new Date(publishedAt);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("pl-PL", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
}

function getItemCount(version: OfferVersion): number {
  if (version.lineItemsCount !== undefined) return version.lineItemsCount;
  if (version.lineItems) return version.lineItems.length;
  if (version.itemCount !== undefined) return version.itemCount;
  return 0;
}

export const VersionPickerModal: React.FC<VersionPickerModalProps> = ({
  versions,
  onConfirm,
  onCancel,
}) => {
  // Sort descending by publishedAt (newest first)
  const sorted = [...versions].sort((a, b) => {
    const aTime = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
    const bTime = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
    return bTime - aTime;
  });

  const [selectedId, setSelectedId] = useState<string>(sorted[0]?.id ?? "");

  // Auto-focus the default-selected radio on mount (a11y)
  const defaultRadioRef = useRef<HTMLInputElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    defaultRadioRef.current?.focus();
  }, []);

  // ESC to cancel (a11y)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  // Scroll lock — prevent background scroll while modal is open (a11y)
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // Focus trap — keep Tab/Shift+Tab within the modal (a11y)
  useEffect(() => {
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const modalNode = modalRef.current;
      if (!modalNode) return;
      const focusable = modalNode.querySelectorAll<HTMLElement>(
        'button, input, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, []);

  const handleConfirm = () => {
    if (selectedId) onConfirm(selectedId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="version-picker-title"
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
        data-testid="version-picker-modal"
      >
        <h3
          id="version-picker-title"
          className="text-lg font-semibold text-neutral-900 mb-4"
        >
          Wybierz wersję źródłową
        </h3>

        <div className="space-y-3 mb-6" role="radiogroup" aria-labelledby="version-picker-title">
          {sorted.map((version, index) => {
            const versionNumber =
              version.versionNumber ?? sorted.length - index;
            const publishedDate = formatPublishedAt(version.publishedAt);
            const netValue = formatCurrency(version.netValue);
            const itemCount = getItemCount(version);
            const isDefault = index === 0;

            return (
              <label
                key={version.id}
                className="flex items-start gap-3 p-3 rounded-lg border border-neutral-200 hover:bg-neutral-50 cursor-pointer transition-colors"
                data-testid={`version-option-${version.id}`}
              >
                <input
                  ref={isDefault ? defaultRadioRef : undefined}
                  type="radio"
                  name="sourceVersion"
                  value={version.id}
                  checked={selectedId === version.id}
                  onChange={() => setSelectedId(version.id)}
                  className="mt-0.5 h-4 w-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
                  data-testid={`version-radio-${version.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-neutral-900">
                      Wersja {versionNumber}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {publishedDate}
                    </span>
                  </div>
                  {version.title && (
                    <p className="text-sm text-neutral-700 mt-0.5 truncate">
                      {version.title}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500">
                    <span>{netValue}</span>
                    <span>{itemCount} pozycji</span>
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            data-testid="version-picker-cancel"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedId}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="version-picker-confirm"
          >
            Potwierdź
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionPickerModal;
